import React, { useState, useEffect, useRef } from "react";
import PatientHomePage from "./PatientHomePage";
import PatientAppointment from "./PatientAppointment";
import PatientChat from "./PatientChat";
import PatientEducateRebuilt from "./PatientEducateRebuilt";
import PatientPrescription from "./PatientPrescription";
import PatientProfile from "./PatientProfile";
import MedicalRecords from "./MedicalRecords";
import BillingView from "./BillingView";
import HelpSupportView from "./HelpSupportView";
import SideBar from "./SideBar";
import Youroheader from '../Youro-header';

import { useLocation } from "react-router-dom";

import { useChatContext } from "../../context/ChatContext";

const PatientView = () => {
  const { setSelectedChat, totalMsgCount } = useChatContext();
  const location = useLocation();
  // Callers can land the patient on a specific view — the pricing page uses this
  // to drop a returning member straight onto the membership panel.
  const [view, setView] = useState(location.state?.view ?? 0);

  // A care plan entry can ask the education tab to open one specific treatment,
  // identified by { diagName, presName }. changeView stays backward compatible:
  // existing callers pass a view number only, and the target simply clears.
  const [educationFocusTreatment, setEducationFocusTreatment] = useState(null);

  const changeView = (nextView, focusTreatment = null) => {
    setEducationFocusTreatment(focusTreatment);
    setView(nextView);
  };

  // The profile menu lives in the header, so the screens it opens need a way to
  // ask for it back. Without this their back buttons had to guess — one had no
  // handler at all, one walked browser history into the sign-in page, and two
  // landed on Personal Information whichever screen you started from.
  const headerRef = useRef(null);
  const openProfileMenu = () => headerRef.current?.openProfile();

  // Title mapping for the header
  const VIEW_TITLES = {
    0: "Home",
    1: "Scheduling",
    2: "Messaging",
    3: "Education",
    4: "Profile",
    5: "Prescriptions",
    6: "Help & Support",
    7: "Medical Records",
    8: "Billing"
  };

  const pageTitle = VIEW_TITLES[view] || "";

  useEffect(() => {
    if (view === 2) return;
    setSelectedChat(null);
  }, [view, setSelectedChat]);

  // ===== Inline styles to keep header at top + only content scrolls =====
  const styles = {
    hm: {
      display: "flex",
      height: "100vh",
      width: "100%",
      overflow: "hidden", // prevents body scroll; only content scrolls
    },
    sidebar: {
      height: "100vh",
      overflowY: "auto", // sidebar can scroll if needed
    },
    main: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      height: "100vh",
      overflow: "hidden", // important: only mainContent scrolls
    },
    headerWrap: {
      position: "sticky",
      top: 0,
      zIndex: 1000,
      background: "#fff", // avoids transparency while scrolling
      flexShrink: 0,
    },
    mainContent: {
      flex: 1,
      overflowY: "auto",
      minHeight: 0, // critical for flex scroll behavior
    },
  };

  return (
    <div className="hm" style={styles.hm}>
      <div className="sidebar" style={styles.sidebar}>
        {/* Routed through changeView, not setView, so navigating away clears any
            pending education focus. Otherwise a stale request would re-open that
            treatment's popup the next time the patient opened Education. */}
        <SideBar active={view} setActive={changeView} count={totalMsgCount} />
      </div>

      <div className="main" style={styles.main}>
        <div style={styles.headerWrap}>
          <Youroheader ref={headerRef} pageTitle={pageTitle} setView={changeView} />
        </div>

        <div style={styles.mainContent}>
          {view === 0 && <PatientHomePage changeView={changeView} />}
          {view === 1 && <PatientAppointment changeView={changeView} />}
          {view === 2 && <PatientChat changeView={changeView} />}
          {view === 3 && (
            <PatientEducateRebuilt
              changeView={changeView}
              focusTreatment={educationFocusTreatment}
            />
          )}
          {/* The screens below are all opened from the profile menu, so their
              back buttons reopen it rather than picking some other view. */}
          {view === 4 && <PatientProfile changeView={changeView} openProfileMenu={openProfileMenu} />}
          {view === 5 && <PatientPrescription changeView={changeView} openProfileMenu={openProfileMenu} />}
          {view === 6 && <HelpSupportView changeView={changeView} openProfileMenu={openProfileMenu} />}
          {view === 7 && <MedicalRecords changeView={changeView} openProfileMenu={openProfileMenu} />}
          {view === 8 && <BillingView changeView={changeView} openProfileMenu={openProfileMenu} />}
        </div>
      </div>
    </div>
  );
};

export default PatientView;